import { GetStaticProps, InferGetStaticPropsType, NextPage } from "next";
import { MCPermissions } from "~/common/constants";
import {
  AnalyticsContentProperties,
  SetAnalyticsDataForContent,
  apiV1ToAnalyticsContentProperties,
} from "~/components/Analytics";
import { apiToAssetDownloadV1Props } from "~/components/AssetDownload";
import { apiToAuthorBioProps } from "~/components/AuthorBio";
import { postsToContentPromoProps } from "~/components/ContentPromo/postToContentPromoProps";
import { apiAnalystReportToKeyTakeawaysCardProps } from "~/components/KeyTakeawaysCard";
import {
  PageMeta,
  PostMetadata,
  apiPostToPageMetadata,
} from "~/components/PageMeta";
import { apiToPostEditCTAProps } from "~/components/PostEditCTA/apiToPostEditCTAProps";
import { apiToContentSummaryProps } from "~/components/ReportPreviewContentSummary/apiToContentSummaryProps";
import { ReportPreviewDownloadButtonProps } from "~/components/ReportPreviewDownloadButton";
import { ReportPreviewHeaderProps } from "~/components/ReportPreviewHeader";
import { apiToReportPreviewHeroProps } from "~/components/ReportPreviewHero/apiToReportPreviewHeroProps";
import {
  ReportPreviewPage,
  ReportPreviewPageProps,
} from "~/components/ReportPreviewPage";
import { apiToTagsProps } from "~/components/Tags/apiToTagsProps";
import { commonConfig } from "~/config/common-config";
import {
  fetchAnalystReports,
  fetchAnalystReportsPreview,
} from "~/lib/server/asgardApiCalls";
import { createGetStaticPathsForPosts } from "~/lib/server/createGetStaticPathsForPosts";
import { wrapGetStaticProps } from "~/lib/server/wrapGetProps";
import { getPrimaryTaxonomy } from "~/lib/shared/getPrimaryTaxonomy";
import { getRouteFromPostType } from "~/lib/shared/getRouteFromPostType";
import { getValueOrDefault } from "~/lib/shared/getValueOrDefault";
import { heDecodeOrDefault } from "~/lib/shared/html-utils";
import { PostTypeEnum } from "~/types/asgard/PostType";

export type PageParams = {
  analystReportParam: string;
};

export const getStaticPaths = createGetStaticPathsForPosts(
  PostTypeEnum.AnalystReport,
  PostTypeEnum.AnalystReport,
  "slug"
);

type StaticProps = ReportPreviewPageProps & {
  meta: PostMetadata;
  analytics: AnalyticsContentProperties;
};

export const getStaticProps: GetStaticProps<StaticProps, PageParams> =
  wrapGetStaticProps(async (req) => {
    const { params, preview } = req;
    const { analystReportParam } = params!;

    const fetchMethod = preview
      ? fetchAnalystReportsPreview
      : fetchAnalystReports;
    const { data: reportData } = await fetchMethod(analystReportParam!);

    const authorBiosProps = apiToAuthorBioProps(reportData.authors);
    const reportHeaderProps: ReportPreviewHeaderProps = {
      kicker: heDecodeOrDefault(
        getPrimaryTaxonomy(reportData.categories)?.name
      ),
      headline: heDecodeOrDefault(reportData.title.rendered),
      date: reportData.date,
      publicationInfo: heDecodeOrDefault(reportData.acf.publication_info),
      subhead: heDecodeOrDefault(reportData.acf.report_intro),
      darkMode: reportData.acf.dark_mode || false,
      disableDate: reportData.acf.disable_date || false,
      isChartPack: reportData.acf.is_chart_pack || false,
    };

    const postEditCTAProps = await apiToPostEditCTAProps({
      isPreview: preview,
      postId: reportData.id,
      postParam: analystReportParam,
    });

    const downloadButtonProps: ReportPreviewDownloadButtonProps = reportData
      ?.acf?.report_assets?.[0]?.file_upload
      ? {
          attachmentId:
            reportData.acf.report_assets[0].file_upload.id.toString(),
          postId: reportData.id.toString(),
          dataAccess: reportData.acf.report_assets[0].permission,
        }
      : {
          attachmentId: getValueOrDefault(
            reportData.acf?.report_asset?.toString(),
            ""
          ),
          postId: reportData.id.toString(),
          dataAccess: [MCPermissions.PRO_FULL_ACCESS_PERM],
        };

    return {
      props: {
        meta: apiPostToPageMetadata(reportData),
        keyTakeawaysProps: apiAnalystReportToKeyTakeawaysCardProps(
          reportData.acf.key_takeaways
        ),
        assetDownloadProps: apiToAssetDownloadV1Props({
          downloadableAssets: reportData.acf.downloadable_assets,
          postId: reportData.id,
          postName: reportData.title.rendered,
          assetSummary: reportData.acf.asset_summary,
        }),
        postEditCTAProps,
        authorBiosProps,
        tagsProps: apiToTagsProps(reportData.tags),
        reportHeaderProps,
        contentPromoProps: postsToContentPromoProps(reportData.relatedStories),
        contentBlocks: reportData.acf.post_content_blocks ?? [],
        contentSummaryProps: apiToContentSummaryProps(reportData),
        downloadButtonProps,
        downloadDescription: heDecodeOrDefault(reportData.acf.download_cta),
        methodology: heDecodeOrDefault(reportData.acf.methodology),
        summary: heDecodeOrDefault(reportData.acf.summary),
        heroProps: apiToReportPreviewHeroProps(reportData),
        shareUrl: new URL(
          getRouteFromPostType(reportData.type, reportData.slug),
          commonConfig.NEXT_PUBLIC_ENDIL_URL
        ).toString(),
        analytics: apiV1ToAnalyticsContentProperties(reportData),
        postId: reportData.id,
        preview: getValueOrDefault(preview, false),
      } satisfies StaticProps,
    };
  });

const AnalystReportPage: NextPage<
  InferGetStaticPropsType<typeof getStaticProps>
> = ({ analytics, meta, ...props }) => (
  <>
    <SetAnalyticsDataForContent {...analytics} />
    <PageMeta {...meta} />
    <ReportPreviewPage {...props} />
  </>
);

export default AnalystReportPage;
